(() => {
  if (customElements.get('rebuild-mobile-navigation')) return;
  class RebuildMobileNavigation extends HTMLElement {
    connectedCallback() {
      this.onReady = () => this.initialize();
      this.onStyleLoad = event => { if (event.target instanceof HTMLLinkElement && event.target.href.includes('rebuild-mobile-navigation.css')) this.initialize(); };
      document.addEventListener('load', this.onStyleLoad, true);
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      else this.initialize();
    }
    initialize() {
      if (this.enhanced || !this.isConnected || getComputedStyle(this).getPropertyValue('--rebuild-mobile-ready').trim() !== '1') return;
      this.drawer = this.closest('[data-mobile-nav]');
      this.scroller = this.closest('[data-mobile-nav-panel]');
      this.fallback = this.parentElement.querySelector(':scope > ul[data-navmenu]');
      this.panels = new Map([...this.querySelectorAll('[data-rebuild-panel]')].map(panel => [panel.id, panel]));
      this.rootPanel = this.panels.get(this.dataset.rootPanel);
      if (!this.drawer || !this.scroller || !this.fallback || !this.rootPanel || [...this.querySelectorAll('[data-rebuild-panel-target]')].some(button => !this.panels.has(button.dataset.rebuildPanelTarget))) return;
      this.masthead = this.drawer.querySelector('[data-rebuild-mobile-masthead]');
      this.cartCount = this.drawer.querySelector('[data-rebuild-mobile-cart-count]');
      this.accountActions = this.scroller.querySelector(':scope > .site-header-actions');
      this.rootOnly = new Map();
      this.syncRootOnly = () => {
        const selector = ':scope > .mobile-nav-content__secondary-menu, :scope > .mobile-nav-content__border, :scope > .utility-bar__mobile-nav, :scope > .utility-bar__mobile__border';
        for (const element of this.parentElement.querySelectorAll(selector)) {
          if (!this.rootOnly.has(element)) this.rootOnly.set(element, { hidden: element.hidden, inert: element.inert });
        }
        for (const [element, previous] of this.rootOnly) {
          element.hidden = this.activePanel !== this.rootPanel || previous.hidden;
          element.inert = this.activePanel !== this.rootPanel || previous.inert;
        }
      };
      // Empire may inject the utility menu after this component has connected.
      this.contentObserver = new MutationObserver(this.syncRootOnly);
      this.contentObserver.observe(this.parentElement, { childList: true });
      if (this.accountActions) {
        this.accountPlaceholder = document.createComment('Rebuild mobile account fallback position');
        this.accountActions.before(this.accountPlaceholder);
        this.rootPanel.append(this.accountActions);
      }
      this.updateCartCount = count => {
        const value = Number(count);
        if (!this.cartCount || !Number.isInteger(value) || value < 0) return;
        this.cartCount.textContent = String(value);
        this.cartCount.hidden = value === 0;
      };
      this.onCartCount = event => { if (event.detail && Object.prototype.hasOwnProperty.call(event.detail, 'item_count')) this.updateCartCount(event.detail.item_count); };
      const mainCartCount = document.querySelector('[data-header-cart-count]');
      if (mainCartCount) this.updateCartCount(mainCartCount.dataset.headerCartCount || 0);
      window.addEventListener('cartcount:update', this.onCartCount);
      if (this.masthead) this.masthead.hidden = false;
      this.stack = [];
      this.onClick = event => {
        const opener = event.target.closest('[data-rebuild-panel-target]');
        if (opener && this.contains(opener)) {
          event.preventDefault();
          const panel = this.panels.get(opener.dataset.rebuildPanelTarget);
          this.stack.push({ panel: this.activePanel, opener, scrollTop: this.scroller.scrollTop });
          opener.setAttribute('aria-expanded', 'true');
          this.show(panel);
          this.scroller.scrollTop = 0;
          panel.querySelector('[data-rebuild-back]').focus({ preventScroll: true });
        } else if (event.target.closest('[data-rebuild-back]')) {
          event.preventDefault();
          const previous = this.stack.pop();
          if (!previous) return;
          previous.opener.setAttribute('aria-expanded', 'false');
          this.show(previous.panel);
          this.scroller.scrollTop = previous.scrollTop;
          previous.opener.focus({ preventScroll: true });
        }
      };
      // Empire keeps outer containment; recompute only its changing Tab endpoints.
      this.onTab = event => {
        if (event.key !== 'Tab' || this.drawer.dataset.open !== 'true') return;
        const visible = [...this.drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter(el => !el.closest('[hidden],[inert]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        if (!visible.length) return;
        const first = visible[0], last = visible[visible.length - 1];
        if ((event.shiftKey && (document.activeElement === first || document.activeElement === this.drawer)) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          event.stopPropagation();
          (event.shiftKey ? last : first).focus();
        }
      };
      this.onDrawerState = () => { if (this.drawer.dataset.open !== this.lastOpenState) { this.lastOpenState = this.drawer.dataset.open; this.reset(); } };
      this.onBreakpoint = event => { if (event.matches) this.reset(); };
      this.addEventListener('click', this.onClick);
      this.drawer.addEventListener('keydown', this.onTab, true);
      this.observer = new MutationObserver(this.onDrawerState);
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['data-open'] });
      this.breakpoint = matchMedia('(min-width: 1024px)');
      this.breakpoint.addEventListener('change', this.onBreakpoint);
      this.fallback.dataset.rebuildMobileFallback = '';
      this.fallback.hidden = true;
      this.fallback.inert = true;
      this.drawer.classList.add('rebuild-mobile-ready');
      this.reset();
      this.hidden = false;
      this.enhanced = true;
      document.removeEventListener('load', this.onStyleLoad, true);
    }
    show(panel) {
      for (const current of this.panels.values()) {
        current.hidden = current !== panel;
        current.inert = current !== panel;
        current.setAttribute('aria-hidden', String(current !== panel));
      }
      this.activePanel = panel;
      this.syncRootOnly();
    }
    reset() {
      this.stack = [];
      this.querySelectorAll('[data-rebuild-panel-target]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.show(this.rootPanel);
    }
    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
      document.removeEventListener('load', this.onStyleLoad, true);
      this.hidden = true;
      if (!this.enhanced) return;
      window.removeEventListener('cartcount:update', this.onCartCount);
      if (this.masthead) this.masthead.hidden = true;
      this.contentObserver.disconnect();
      for (const [element, previous] of this.rootOnly) { element.hidden = previous.hidden; element.inert = previous.inert; }
      if (this.accountPlaceholder) { this.accountPlaceholder.replaceWith(this.accountActions); this.accountPlaceholder = null; }
      this.removeEventListener('click', this.onClick);
      this.drawer.removeEventListener('keydown', this.onTab, true);
      this.observer.disconnect();
      this.breakpoint.removeEventListener('change', this.onBreakpoint);
      this.fallback.hidden = false;
      this.fallback.inert = false;
      delete this.fallback.dataset.rebuildMobileFallback;
      this.drawer.classList.remove('rebuild-mobile-ready');
      this.enhanced = false;
    }
  }
  customElements.define('rebuild-mobile-navigation', RebuildMobileNavigation);
})();
